import SEOHead from "@/components/SEOHead";

const TECHNIQUES = [
  {
    n: "01",
    title: "Mirrored Drawing",
    tag: "Two hands, one image",
    desc: "Both hands create opposite portions of the same image simultaneously — like two draftsmen sharing a single mind. The composition emerges from the center outward.",
    img: "https://img.youtube.com/vi/7WKmAlL12Jg/maxresdefault.jpg",
  },
  {
    n: "02",
    title: "Independent Drawing",
    tag: "Two hands, two subjects",
    desc: "Each hand creates a different image at the same time. One may be sketching a landscape while the other is drawing a face — no mirrors, no duplicated video.",
    img: "https://img.youtube.com/vi/ymQ74gijMtk/maxresdefault.jpg",
  },
  {
    n: "03",
    title: "Ambidextrous Drawing",
    tag: "Either hand, alone",
    desc: "Colin can draw, write or paint with either hand independently. Left or right, alone or in sequence, without a dominant-hand crutch.",
    img: "https://img.youtube.com/vi/ZOuhCDn9HWI/maxresdefault.jpg",
  },
  {
    n: "04",
    title: "Multi-Tool Experiments",
    tag: "Two hands, many tools",
    desc: "Works involving multiple pens, markers, brushes or other materials held simultaneously — the process itself becomes part of the composition.",
    img: "https://img.youtube.com/vi/FhyFZFuWNLM/maxresdefault.jpg",
  },
];

export default function Techniques() {
  return (
    <>
      <SEOHead
        title="How Does He Do It? | Colin Darke — The Ambidextrous Artist"
        description="The four core techniques of Colin Darke's ambidextrous practice: mirrored drawing, independent drawing, ambidextrous drawing, and multi-tool experiments. No mirrors. No duplicated video. Two hands working in real time."
        path="/techniques"
      />

      <section className="pt-16 pb-8 border-b border-[hsl(var(--line))]">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-16">
          <p className="catalog-label mb-4">The ability</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] max-w-4xl">
            How does he
            <br />
            <span className="italic text-[hsl(var(--vermillion))]">
              do it?
            </span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-[hsl(var(--ink-muted))] leading-relaxed">
            No mirrors. No duplicated video. Two hands working in real time.
            Below are the four core forms of Colin's ambidextrous practice.
          </p>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-16 space-y-24">
          {TECHNIQUES.map((t, i) => (
            <article
              key={t.n}
              className={`grid lg:grid-cols-12 gap-10 items-center ${
                i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
              }`}
            >
              <figure className="lg:col-span-7 relative">
                <div className="w-full bg-[hsl(var(--bg-secondary))] flex items-center justify-center overflow-hidden" style={{ aspectRatio: "4/3" }}>
                  <img
                    src={t.img}
                    alt={`${t.title} — a demonstration by Colin Darke, ${t.tag.toLowerCase()}.`}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
                <figcaption className="absolute top-4 left-4 catalog-label bg-[hsl(var(--bg))]/90 backdrop-blur px-3 py-1">
                  {t.n} · Technique
                </figcaption>
              </figure>
              <div className="lg:col-span-5">
                <p className="catalog-label mb-3">{t.tag}</p>
                <h2 className="font-serif text-4xl sm:text-5xl leading-[1.05]">
                  {t.title}
                </h2>
                <p className="mt-6 text-lg text-[hsl(var(--ink-muted))] leading-relaxed">
                  {t.desc}
                </p>
                <p className="mt-6 catalog-label text-[hsl(var(--vermillion))]">
                  Filmed live · one continuous take
                </p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Popular videos */}
      <section className="py-16 sm:py-24 bg-[hsl(var(--bg-vault))] text-[hsl(var(--bg))]">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-16">
          <p className="catalog-label text-[hsl(var(--bg))]/60 mb-4">
            Watch him work
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl leading-tight max-w-3xl">
            Selected videos from the archive.
          </h2>
          <p className="mt-4 max-w-2xl text-[hsl(var(--bg))]/70">
            The Good Morning America segment plus three of Colin's most-viewed
            short-form videos.
          </p>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="videos-grid">
            <VideoTile embed="https://www.youtube.com/embed/7WKmAlL12Jg" label="Good Morning America · 2021" testid="video-gma" />
            <VideoTile embed="https://www.youtube.com/embed/ZOuhCDn9HWI" label="Short · viral clip" testid="video-short-1" />
            <VideoTile embed="https://www.youtube.com/embed/ymQ74gijMtk" label="Short · viral clip" testid="video-short-2" />
            <VideoTile embed="https://www.youtube.com/embed/FhyFZFuWNLM" label="Short · viral clip" testid="video-short-3" />
          </div>

          <div className="mt-12 flex flex-wrap items-center gap-4">
            <a
              href="https://www.instagram.com/p/C_A8yfIO_OJ/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-[hsl(var(--bg))]/40 px-5 py-3 font-mono text-xs uppercase tracking-widest hover:bg-[hsl(var(--bg))] hover:text-[hsl(var(--ink))] transition-colors"
              data-testid="link-garyvee-interview"
            >
              Interview with Gary Vaynerchuk →
            </a>
          </div>
        </div>
      </section>

      {/* Transparency note */}
      <section className="py-16 bg-[hsl(var(--bg-secondary))]">
        <div className="mx-auto max-w-[900px] px-4 sm:px-8 lg:px-16 text-center">
          <p className="catalog-label mb-4">Transparency</p>
          <p className="font-serif text-3xl sm:text-4xl leading-snug">
            The two-handed technique is not a special effect.
          </p>
          <p className="mt-4 text-[hsl(var(--ink-muted))] max-w-2xl mx-auto">
            Colin's videos are filmed in a single, continuous take. He does
            not use mirrors, mirroring in post-production, split-screen, or
            AI generation. The point of the work is what actually happens on
            the paper, in real time.
          </p>
        </div>
      </section>
    </>
  );
}

function VideoTile({ embed, label, testid }) {
  return (
    <div data-testid={testid}>
      <div className="relative w-full overflow-hidden bg-black" style={{ aspectRatio: "9/16" }}>
        <iframe
          src={embed}
          title={label}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          className="absolute inset-0 w-full h-full"
        />
      </div>
      <p className="mt-3 catalog-label text-[hsl(var(--bg))]/70">{label}</p>
    </div>
  );
}
